import config from "../../config.json";

// AI Wallet (backend: /api/auth/billing/ai-wallet and /admin/ai-*). The
// wallet is prepaid SAR credit for AI features, separate from subscriptions.
// A top-up is paid through the normal invoice receipt upload and approved by
// Finance in the existing payment review; nothing here moves money directly.

const BASE = `${config.BASE_AUTH_API}billing`;

async function request(endpoint, { method = "GET", body } = {}) {
  const token = localStorage.getItem("auth-token");
  const response = await fetch(`${BASE}${endpoint}`, {
    method,
    cache: "no-store",
    headers: { Accept: "application/json", Authorization: `Bearer ${token}`, ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const error = new Error(data.message || "AI Wallet request failed");
    error.code = data.code;
    error.status = response.status;
    error.details = data.details;
    throw error;
  }
  return data.data;
}

const query = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, value);
  });
  const text = params.toString();
  return text ? `?${text}` : "";
};

// A key that makes a repeated submit of the same form harmless.
export const newIdempotencyKey = (prefix = "ui") => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// ------------------------------------------------------------- restaurant
export const getAiWalletAPI = (storeId) => request(`/ai-wallet${query({ storeId })}`);
export const listAiWalletEntriesAPI = (storeId, filters = {}) => request(`/ai-wallet/entries${query({ storeId, ...filters })}`);
export const listAiWalletUsageAPI = (storeId, filters = {}) => request(`/ai-wallet/usage${query({ storeId, ...filters })}`);
export const listAiWalletTopUpsAPI = (storeId, filters = {}) => request(`/ai-wallet/top-ups${query({ storeId, ...filters })}`);
export const getAiWalletTopUpAPI = (id) => request(`/ai-wallet/top-ups/${id}`);
export const previewAiWalletTopUpAPI = (storeId, amountSar) => request("/ai-wallet/top-ups/preview", { method: "POST", body: { storeId, amountSar } });
export const requestAiWalletTopUpAPI = (storeId, amountSar, idempotencyKey) => request("/ai-wallet/top-ups", { method: "POST", body: { storeId, amountSar, idempotencyKey } });
export const cancelAiWalletTopUpAPI = (id, reason = "") => request(`/ai-wallet/top-ups/${id}/cancel`, { method: "POST", body: { reason } });

// -------------------------------------------------------- finance / admin
export const adminListAiWalletsAPI = (filters = {}) => request(`/admin/ai-wallets${query(filters)}`);
export const adminGetAiWalletAPI = (storeId) => request(`/admin/ai-wallets/${encodeURIComponent(storeId)}`);
export const adminListAiWalletEntriesAPI = (storeId, filters = {}) => request(`/admin/ai-wallets/${encodeURIComponent(storeId)}/entries${query(filters)}`);
export const adminListAiTopUpsAPI = (filters = {}) => request(`/admin/ai-wallet/top-ups${query(filters)}`);
export const adminListAiChargesAPI = (filters = {}) => request(`/admin/ai-charges${query(filters)}`);
export const adminGetAiChargeAPI = (id) => request(`/admin/ai-charges/${id}`);
export const adminGetAiCostSummaryAPI = (filters = {}) => request(`/admin/ai-cost/summary${query(filters)}`);
// Shadow / enforce analysis per feature: cost distribution, hypothetical charges, hold and wallet sufficiency.
export const adminGetAiRolloutReportAPI = (filters = {}) => request(`/admin/ai-rollout/report${query(filters)}`);
export const adminRefundAiUsageAPI = (storeId, body) => request(`/admin/ai-wallets/${encodeURIComponent(storeId)}/refunds`, { method: "POST", body });
export const adminAdjustAiWalletAPI = (storeId, body) => request(`/admin/ai-wallets/${encodeURIComponent(storeId)}/adjustments`, { method: "POST", body });
export const adminReverseAiWalletEntryAPI = (entryId, body) => request(`/admin/ai-wallets/entries/${entryId}/reverse`, { method: "POST", body });
export const adminSetAiWalletStatusAPI = (storeId, body) => request(`/admin/ai-wallets/${encodeURIComponent(storeId)}`, { method: "PATCH", body });
export const adminListAiReconciliationAPI = (filters = {}) => request(`/admin/ai-reconciliation${query(filters)}`);
export const adminResolveAiReconciliationAPI = (id, body) => request(`/admin/ai-reconciliation/${id}/resolve`, { method: "POST", body });
export const adminListAiPricingAPI = () => request("/admin/ai-pricing");
export const adminCreateAiPricingAPI = (body) => request("/admin/ai-pricing", { method: "POST", body });
export const adminActivateAiPricingAPI = (id) => request(`/admin/ai-pricing/${id}/activate`, { method: "POST" });
